import { createTheme, alpha } from '@mui/material/styles';

// Visitio — system wizualny „śliwka i szampan”.
//
// Zasady (z analizy UX zaakceptowanej 2026-09-12):
// - kolor znaczy, a nie dekoruje: śliwka = akcja, zieleń/bursztyn/czerwień = stan,
//   szampan = jedyny akcent premium, uzywany oszczednie;
// - Manrope w calym interfejsie, Bodoni Moda wylacznie w tytule strony (h1);
// - tekst nie schodzi ponizej 12 px, podstawowy ma 15 px;
// - strony biora kolory z `tokens` zamiast wpisywac kody na sztywno.

declare module '@mui/material/styles' {
  interface Palette {
    accent: Palette['primary'];
    neutral: { 50: string; 100: string; 200: string; 300: string; 400: string; 500: string; 600: string; 700: string; 800: string; 900: string };
  }
  interface PaletteOptions {
    accent?: PaletteOptions['primary'];
    neutral?: { 50: string; 100: string; 200: string; 300: string; 400: string; 500: string; 600: string; 700: string; 800: string; 900: string };
  }
}

export const DISPLAY_FONT = '"Bodoni Moda", "Bodoni 72", Didot, Georgia, serif';
export const UI_FONT = '"Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

export const tokens = {
  // tekst i linie
  ink: '#1C1726',
  inkSoft: '#4A4358',
  muted: '#6B6479',
  subtle: '#9A93A8',
  line: '#E9E5F0',
  lineStrong: '#D8D2E3',
  // powierzchnie
  ground: '#F6F5F9',
  paper: '#FFFFFF',
  surface: '#FAF9FC',
  surfaceStrong: '#F1EFF5',
  // marka
  plum: '#54409E',
  plumDark: '#3F2F7E',
  plumLight: '#7A66C2',
  plumSoft: '#EFEBF9',
  plumSofter: '#F7F5FC',
  champagne: '#C9A46A',
  champagneDeep: '#8A6630',
  champagneSoft: '#F6EFE3',
  aubergine: '#17121F',
  aubergineRaised: '#241B33',
  // stany
  success: '#2E7D5B',
  successSoft: '#E3F1EA',
  warning: '#C98A1F',
  warningText: '#8A5A10',
  warningSoft: '#F6EEDC',
  danger: '#B03F39',
  dangerSoft: '#F8E8E6',
  info: '#3B6FB6',
  infoSoft: '#E6EEF8',
} as const;

// Kolory kategorii na wykresach — stonowane, rozroznialne, w kolejnosci uzycia.
export const CHART_PALETTE = ['#54409E', '#2E7D5B', '#C9A46A', '#4F7CAC', '#B0605A', '#3E8E8A', '#9C8ADB', '#B7791F'];

// Kolory osob (pracownicy w kalendarzu, awatary klientow). Wszystkie czytelne z bialym tekstem.
export const PERSON_COLORS = ['#54409E', '#2E7D5B', '#A5574F', '#3E6FA0', '#9A6F2A', '#7A5AB5', '#347F7B', '#A44F78'];

export const personColor = (seed: string | number | undefined | null): string => {
  if (typeof seed === 'number') return PERSON_COLORS[Math.abs(seed) % PERSON_COLORS.length];
  const s = String(seed ?? '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PERSON_COLORS[h % PERSON_COLORS.length];
};

const t = tokens;

export const PREMIUM_COLORS = {
  sidebar: {
    bg: t.aubergine,
    active: '#2B223A',
    activeGlow: alpha(t.champagne, 0.18),
    accent: t.champagne,
    text: '#B7AFC6',
    textDim: '#8E859E',
    textActive: '#FFFFFF',
    hoverBg: alpha('#FFFFFF', 0.05),
    divider: alpha('#FFFFFF', 0.07),
    badge: t.champagne,
  },
  status: {
    confirmed: { bg: t.successSoft, text: t.success, dot: '#3A9A6F' },
    pending: { bg: t.warningSoft, text: t.warningText, dot: t.warning },
    inProgress: { bg: t.plumSoft, text: t.plum, dot: '#6F5BC4' },
    completed: { bg: t.surfaceStrong, text: t.inkSoft, dot: '#8E859E' },
    cancelled: { bg: t.dangerSoft, text: t.danger, dot: '#C4554F' },
    noShow: { bg: '#F5E9E4', text: '#9B4A2E', dot: '#BF6A4A' },
    paid: { bg: t.successSoft, text: t.success, dot: '#3A9A6F' },
    unpaid: { bg: t.warningSoft, text: t.warningText, dot: t.warning },
  },
  calendar: {
    slotBorder: '#EEEBF3',
    nowLine: t.danger,
    eventDefault: t.plum,
    eventConfirmed: t.success,
    eventPending: t.warning,
    eventInProgress: '#6F5BC4',
  },
  // Pozostawione dla zgodnosci — nowe ekrany nie uzywaja gradientow.
  gradients: {
    primary: `linear-gradient(135deg, ${t.plum} 0%, ${t.plumLight} 100%)`,
    hero: `linear-gradient(160deg, ${t.aubergineRaised} 0%, ${t.aubergine} 100%)`,
    revenue: `linear-gradient(135deg, ${t.success} 0%, #4E9C7A 100%)`,
    clients: `linear-gradient(135deg, ${t.champagneDeep} 0%, ${t.champagne} 100%)`,
    bookings: `linear-gradient(135deg, #3E5F99 0%, #5F7FB8 100%)`,
  },
};

const focusRing = `0 0 0 4px ${alpha(t.plum, 0.14)}`;

export const theme = createTheme({
  palette: {
    primary: { main: t.plum, light: t.plumLight, dark: t.plumDark, contrastText: '#FFFFFF' },
    secondary: { main: t.success, light: '#4E9C7A', dark: '#23604A', contrastText: '#FFFFFF' },
    accent: { main: t.champagne, light: '#DEC196', dark: t.champagneDeep, contrastText: t.ink },
    success: { main: t.success, light: '#4E9C7A', dark: '#23604A', contrastText: '#FFFFFF' },
    warning: { main: t.warning, light: '#E0AE55', dark: t.warningText, contrastText: '#FFFFFF' },
    error: { main: t.danger, light: '#C9675F', dark: '#8E302B', contrastText: '#FFFFFF' },
    info: { main: t.info, light: '#6390CB', dark: '#2F5A94', contrastText: '#FFFFFF' },
    background: { default: t.ground, paper: t.paper },
    text: { primary: t.ink, secondary: t.muted, disabled: '#A39DB0' },
    divider: t.line,
    action: {
      active: t.muted,
      hover: alpha(t.plum, 0.05),
      selected: alpha(t.plum, 0.09),
      focus: alpha(t.plum, 0.12),
      disabled: '#A39DB0',
      disabledBackground: '#EDEAF2',
    },
    neutral: {
      50: t.surface, 100: t.ground, 200: t.line, 300: t.lineStrong,
      400: t.subtle, 500: t.muted, 600: t.inkSoft, 700: '#352E42',
      800: t.aubergineRaised, 900: t.aubergine,
    },
  },
  typography: {
    fontFamily: UI_FONT,
    fontSize: 15,
    h1: {
      fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: '1.875rem', lineHeight: 1.2, letterSpacing: '-0.01em',
      fontVariationSettings: '"opsz" 48',
      '@media (max-width:599.95px)': { fontSize: '1.625rem' },
    },
    h2: { fontWeight: 700, fontSize: '1.375rem', lineHeight: 1.3, letterSpacing: '-0.01em' },
    h3: { fontWeight: 700, fontSize: '1.25rem', lineHeight: 1.35, letterSpacing: '-0.005em' },
    h4: { fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.4 },
    h5: { fontWeight: 700, fontSize: '1.0625rem', lineHeight: 1.4 },
    h6: { fontWeight: 700, fontSize: '1rem', lineHeight: 1.45 },
    subtitle1: { fontWeight: 500, fontSize: '0.9375rem', lineHeight: 1.5, color: t.muted },
    subtitle2: { fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.5, color: t.muted },
    body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.55 },
    caption: { fontSize: '0.8125rem', lineHeight: 1.45, fontWeight: 500, color: t.muted },
    overline: { fontSize: '0.75rem', lineHeight: 1.5, fontWeight: 700, letterSpacing: '0.1em', color: t.muted },
    button: { textTransform: 'none', fontWeight: 650, fontSize: '0.9375rem', letterSpacing: 0 },
  },
  shape: { borderRadius: 12 },
  shadows: [
    'none',
    '0 1px 2px rgba(28,23,38,0.04)',
    '0 1px 3px rgba(28,23,38,0.06), 0 1px 2px rgba(28,23,38,0.04)',
    '0 4px 12px rgba(28,23,38,0.06), 0 1px 3px rgba(28,23,38,0.04)',
    '0 8px 20px rgba(28,23,38,0.08), 0 2px 4px rgba(28,23,38,0.04)',
    '0 10px 26px rgba(28,23,38,0.10), 0 2px 5px rgba(28,23,38,0.05)',
    '0 12px 30px rgba(28,23,38,0.11), 0 2px 6px rgba(28,23,38,0.05)',
    '0 12px 32px rgba(28,23,38,0.12), 0 2px 6px rgba(28,23,38,0.06)',
    '0 14px 36px rgba(28,23,38,0.12), 0 3px 8px rgba(28,23,38,0.06)',
    ...Array(15).fill('0 18px 44px rgba(28,23,38,0.14), 0 4px 10px rgba(28,23,38,0.06)'),
    '0 24px 64px rgba(28,23,38,0.20), 0 6px 16px rgba(28,23,38,0.08)',
  ] as any,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: t.ground,
          color: t.ink,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
        },
        '::selection': { backgroundColor: alpha(t.plum, 0.16) },
        '*::-webkit-scrollbar': { width: 8, height: 8 },
        '*::-webkit-scrollbar-track': { background: 'transparent' },
        '*::-webkit-scrollbar-thumb': { background: t.lineStrong, borderRadius: 8, border: '2px solid transparent', backgroundClip: 'content-box' },
        '*::-webkit-scrollbar-thumb:hover': { background: t.subtle, backgroundClip: 'content-box' },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': { transitionDuration: '0.01ms !important', animationDuration: '0.01ms !important' },
        },
      },
    },

    MuiButtonBase: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': { outline: `2px solid ${t.plum}`, outlineOffset: 2 },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 11,
          minHeight: 44,
          padding: '9px 18px',
          fontWeight: 650,
          boxShadow: 'none',
          whiteSpace: 'nowrap',
          transition: 'background-color .15s ease, border-color .15s ease, color .15s ease, box-shadow .15s ease',
          '& .MuiButton-startIcon': { marginRight: 8 },
          '& .MuiButton-endIcon': { marginLeft: 8 },
          '& .MuiButton-startIcon > *:nth-of-type(1), & .MuiButton-endIcon > *:nth-of-type(1)': { fontSize: 20 },
        },
        sizeSmall: {
          minHeight: 36, padding: '6px 14px', fontSize: '0.875rem', borderRadius: 9,
          '& .MuiButton-startIcon > *:nth-of-type(1), & .MuiButton-endIcon > *:nth-of-type(1)': { fontSize: 18 },
        },
        sizeLarge: { minHeight: 50, padding: '12px 24px', fontSize: '1rem', borderRadius: 12 },
        contained: {
          '&:hover': { boxShadow: 'none' },
          '&.Mui-disabled': { backgroundColor: '#EDEAF2', color: '#A39DB0' },
        },
        containedPrimary: {
          backgroundColor: t.plum,
          boxShadow: `inset 0 1px 0 rgba(255,255,255,0.14), 0 4px 12px ${alpha(t.plum, 0.18)}`,
          '&:hover': { backgroundColor: t.plumDark, boxShadow: `inset 0 1px 0 rgba(255,255,255,0.12), 0 6px 16px ${alpha(t.plum, 0.24)}` },
        },
        outlined: {
          borderWidth: 1,
          backgroundColor: t.paper,
          '&:hover': { borderWidth: 1 },
        },
        outlinedPrimary: {
          color: t.ink,
          borderColor: t.lineStrong,
          '&:hover': { borderColor: '#BFB6CF', backgroundColor: t.plumSofter },
        },
        outlinedError: {
          borderColor: alpha(t.danger, 0.4),
          '&:hover': { borderColor: t.danger, backgroundColor: t.dangerSoft },
        },
        text: { padding: '9px 12px' },
        textPrimary: { color: t.plum, '&:hover': { backgroundColor: t.plumSofter } },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 10, color: t.muted, '&:hover': { backgroundColor: alpha(t.plum, 0.06), color: t.ink } },
        sizeSmall: { padding: 6 },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: { backgroundColor: t.paper, borderRadius: 11 },
        grouped: { borderColor: t.lineStrong },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', color: t.muted, borderColor: t.lineStrong,
          padding: '7px 14px', lineHeight: 1.5,
          '&:hover': { backgroundColor: t.plumSofter, color: t.ink },
          '&.Mui-selected': { backgroundColor: t.plumSoft, color: t.plum, '&:hover': { backgroundColor: '#E6E0F6' } },
        },
        sizeSmall: { padding: '5px 12px', fontSize: '0.8125rem' },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: { boxShadow: `0 8px 24px ${alpha(t.plum, 0.3)}`, '&:hover': { boxShadow: `0 10px 28px ${alpha(t.plum, 0.36)}` } },
      },
    },

    MuiPaper: {
      defaultProps: { elevation: 1 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 14 },
        elevation1: { boxShadow: 'none', border: `1px solid ${t.line}` },
        outlined: { borderColor: t.line },
      },
    },
    MuiCard: {
      styleOverrides: { root: { borderRadius: 14, overflow: 'visible' } },
    },
    MuiCardContent: {
      styleOverrides: { root: { padding: 20, '&:last-child': { paddingBottom: 20 } } },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { padding: '16px 20px' },
        title: { fontSize: '1.0625rem', fontWeight: 700 },
        subheader: { fontSize: '0.875rem', color: t.muted },
      },
    },
    MuiAccordion: {
      defaultProps: { disableGutters: true, elevation: 0 },
      styleOverrides: {
        root: { border: `1px solid ${t.line}`, borderRadius: 12, '&:before': { display: 'none' }, '&:not(:last-of-type)': { marginBottom: 8 } },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 18,
          border: 'none',
          boxShadow: '0 24px 64px rgba(28,23,38,0.20), 0 6px 16px rgba(28,23,38,0.08)',
          '@media (max-width:599.95px)': {
            margin: 0,
            width: '100%',
            maxWidth: '100% !important',
            maxHeight: 'calc(100% - 24px)',
            borderRadius: '18px 18px 0 0',
          },
          '&.MuiDialog-paperFullScreen': { borderRadius: 0, maxHeight: '100%' },
        },
        container: {
          '@media (max-width:599.95px)': { alignItems: 'flex-end' },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: UI_FONT, fontSize: '1.25rem', fontWeight: 750, lineHeight: 1.35, padding: '22px 24px 10px', color: t.ink,
          '@media (max-width:599.95px)': { padding: '20px 18px 8px' },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '8px 24px 16px',
          '.MuiDialogTitle-root + &': { paddingTop: 8 },
          '@media (max-width:599.95px)': { padding: '8px 18px 16px' },
        },
        dividers: { borderColor: t.line, padding: '16px 24px' },
      },
    },
    MuiDialogContentText: {
      styleOverrides: { root: { color: t.inkSoft, fontSize: '0.9375rem' } },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 24px 20px',
          gap: 8,
          flexWrap: 'wrap',
          '& > :not(style) ~ :not(style)': { marginLeft: 0 },
          '@media (max-width:599.95px)': { padding: '12px 18px 18px' },
        },
      },
    },

    // Formularze: etykieta nad polem, pole 46 px (male 40 px), wyrazny fokus.
    MuiInputLabel: {
      defaultProps: { shrink: true },
      styleOverrides: {
        root: {
          position: 'relative',
          transform: 'none',
          fontSize: '0.875rem',
          fontWeight: 600,
          lineHeight: 1.4,
          color: t.ink,
          marginBottom: 6,
          whiteSpace: 'normal',
          overflow: 'visible',
          maxWidth: '100%',
          pointerEvents: 'auto',
          '&.Mui-focused': { color: t.ink },
          '&.Mui-error': { color: t.danger },
          '&.Mui-disabled': { color: t.subtle },
        },
        asterisk: { color: t.danger },
        sizeSmall: { fontSize: '0.8125rem' },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: { fontSize: '0.875rem', fontWeight: 600, color: t.ink, '&.Mui-focused': { color: t.ink } },
        asterisk: { color: t.danger },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: { fontSize: '0.9375rem' },
        input: {
          '&::placeholder': { color: t.subtle, opacity: 1 },
          'label[data-shrink=false] + .MuiInputBase-formControl &::placeholder': { opacity: '1 !important' },
        },
      },
    },
    MuiInput: {
      styleOverrides: { formControl: { 'label + &': { marginTop: 0 } } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 11,
          backgroundColor: t.paper,
          transition: 'box-shadow .15s ease',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: t.lineStrong, top: 0, transition: 'border-color .15s ease' },
          '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BFB6CF' },
          '&.Mui-focused': { boxShadow: focusRing },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: t.plum, borderWidth: 1 },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: t.danger },
          '&.Mui-error.Mui-focused': { boxShadow: `0 0 0 4px ${alpha(t.danger, 0.12)}` },
          '&.Mui-disabled': { backgroundColor: t.surface },
        },
        input: { padding: '12px 14px', height: '1.4375em' },
        inputSizeSmall: { padding: '9px 12px' },
        multiline: { padding: '12px 14px' },
        inputMultiline: { padding: 0, height: 'auto' },
        adornedStart: { paddingLeft: 12 },
        adornedEnd: { paddingRight: 12 },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: { borderRadius: 11, backgroundColor: t.surfaceStrong, '&:before, &:after': { display: 'none' } },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: { color: t.muted, '& .MuiTypography-root': { fontSize: '0.875rem', fontWeight: 600, color: t.muted } },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { marginLeft: 0, marginRight: 0, marginTop: 6, fontSize: '0.8125rem', lineHeight: 1.45, color: t.muted, '&.Mui-error': { color: t.danger, fontWeight: 600 } },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: { '&:has(.MuiSwitch-root)': { marginLeft: 0, gap: 12 } },
        label: { fontSize: '0.9375rem' },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: { '&:focus': { backgroundColor: 'transparent' } },
        icon: { color: t.muted },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: { width: 46, height: 28, padding: 0, overflow: 'visible' },
        switchBase: {
          padding: 3,
          color: '#FFFFFF',
          '&.Mui-checked': {
            transform: 'translateX(18px)',
            color: '#FFFFFF',
            '& + .MuiSwitch-track': { backgroundColor: t.plum, opacity: 1 },
          },
          '&.Mui-focusVisible .MuiSwitch-thumb': { outline: `2px solid ${t.plum}`, outlineOffset: 2 },
          '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.4 },
          '&:hover': { backgroundColor: 'transparent' },
        },
        thumb: { width: 22, height: 22, boxShadow: '0 1px 3px rgba(28,23,38,0.25)' },
        track: { borderRadius: 14, backgroundColor: '#D3CCDF', opacity: 1 },
        sizeSmall: {
          width: 38, height: 22,
          '& .MuiSwitch-switchBase': { padding: 3, '&.Mui-checked': { transform: 'translateX(16px)' } },
          '& .MuiSwitch-thumb': { width: 16, height: 16 },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: '#BFB6CF', '&.Mui-checked': { color: t.plum } } },
    },
    MuiRadio: {
      styleOverrides: { root: { color: '#BFB6CF', '&.Mui-checked': { color: t.plum } } },
    },
    MuiSlider: {
      styleOverrides: { thumb: { boxShadow: 'none' }, rail: { backgroundColor: t.lineStrong, opacity: 1 } },
    },

    MuiMenu: {
      styleOverrides: {
        paper: { borderRadius: 12, border: `1px solid ${t.line}`, boxShadow: '0 14px 36px rgba(28,23,38,0.12), 0 3px 8px rgba(28,23,38,0.06)', marginTop: 4 },
        list: { padding: 6 },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: { borderRadius: 12, border: `1px solid ${t.line}`, boxShadow: '0 14px 36px rgba(28,23,38,0.12), 0 3px 8px rgba(28,23,38,0.06)' },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.9375rem',
          borderRadius: 8,
          minHeight: 40,
          '&:hover': { backgroundColor: t.surfaceStrong },
          '&.Mui-selected': { backgroundColor: t.plumSoft, '&:hover': { backgroundColor: '#E6E0F6' } },
          '&.Mui-focusVisible': { backgroundColor: t.surfaceStrong },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: { root: { borderRadius: 10 } },
    },
    MuiListItemIcon: {
      styleOverrides: { root: { color: t.muted, minWidth: 36 } },
    },
    MuiListItemText: {
      styleOverrides: { primary: { fontSize: '0.9375rem' }, secondary: { fontSize: '0.8125rem', color: t.muted } },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { borderRadius: 12, border: `1px solid ${t.line}`, boxShadow: '0 14px 36px rgba(28,23,38,0.12)' },
        option: { fontSize: '0.9375rem', borderRadius: 8, margin: '1px 6px' },
        inputRoot: { '&.MuiOutlinedInput-root': { padding: '5px 9px' }, '&.MuiOutlinedInput-root.MuiInputBase-sizeSmall': { padding: '3px 8px' } },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 999, fontWeight: 600, fontSize: '0.8125rem', height: 30 },
        sizeSmall: { height: 24, fontSize: '0.75rem' },
        label: { paddingLeft: 12, paddingRight: 12 },
        labelSmall: { paddingLeft: 9, paddingRight: 9 },
        filled: { backgroundColor: t.surfaceStrong, color: t.inkSoft },
        outlined: { borderColor: t.lineStrong, backgroundColor: t.paper },
        clickable: { '&:hover': { filter: 'brightness(0.97)' } },
        icon: { fontSize: 16, marginLeft: 8 },
        iconSmall: { fontSize: 14, marginLeft: 6 },
        deleteIcon: { fontSize: 18 },
      },
      // Kolorowe chipy sa miekkie (jasne tlo, ciemny tekst) — status czyta sie od razu,
      // a nie konkuruje z przyciskami.
      variants: [
        { props: { variant: 'filled', color: 'primary' }, style: { backgroundColor: t.plumSoft, color: t.plum } },
        { props: { variant: 'filled', color: 'secondary' }, style: { backgroundColor: t.successSoft, color: t.success } },
        { props: { variant: 'filled', color: 'success' }, style: { backgroundColor: t.successSoft, color: t.success } },
        { props: { variant: 'filled', color: 'error' }, style: { backgroundColor: t.dangerSoft, color: t.danger } },
        { props: { variant: 'filled', color: 'warning' }, style: { backgroundColor: t.warningSoft, color: t.warningText } },
        { props: { variant: 'filled', color: 'info' }, style: { backgroundColor: t.infoSoft, color: t.info } },
        { props: { variant: 'outlined', color: 'primary' }, style: { borderColor: alpha(t.plum, 0.4), color: t.plum } },
        { props: { variant: 'outlined', color: 'success' }, style: { borderColor: alpha(t.success, 0.4), color: t.success } },
        { props: { variant: 'outlined', color: 'error' }, style: { borderColor: alpha(t.danger, 0.4), color: t.danger } },
        { props: { variant: 'outlined', color: 'warning' }, style: { borderColor: alpha(t.warning, 0.5), color: t.warningText } },
      ],
    },
    MuiBadge: {
      styleOverrides: { badge: { fontWeight: 700, fontSize: '0.75rem' } },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { fontWeight: 700, fontSize: '0.875rem' },
        colorDefault: { backgroundColor: t.plumLight, color: '#FFFFFF' },
      },
    },
    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: { backgroundColor: t.ink, borderRadius: 8, fontSize: '0.8125rem', fontWeight: 500, padding: '6px 10px', lineHeight: 1.45 },
        arrow: { color: t.ink },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { height: 6, borderRadius: 99, backgroundColor: t.surfaceStrong },
        bar: { borderRadius: 99 },
      },
    },
    MuiSkeleton: {
      defaultProps: { animation: 'wave' },
      styleOverrides: { root: { backgroundColor: t.surfaceStrong }, rounded: { borderRadius: 10 } },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: t.line } },
    },
    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: { root: { color: t.plum, fontWeight: 600 } },
    },
    MuiBreadcrumbs: {
      styleOverrides: { root: { fontSize: '0.8125rem', color: t.muted }, separator: { marginLeft: 6, marginRight: 6 } },
    },

    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 44 },
        indicator: { height: 2, borderRadius: 2, backgroundColor: t.plum },
        flexContainer: { gap: 4 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none', fontWeight: 600, fontSize: '0.9375rem', minHeight: 44, minWidth: 0,
          padding: '10px 14px', color: t.muted,
          '&:hover': { color: t.ink },
          '&.Mui-selected': { color: t.ink },
        },
      },
    },

    MuiTableContainer: {
      styleOverrides: { root: { WebkitOverflowScrolling: 'touch' } },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${t.line}`, fontSize: '0.90625rem', padding: '14px 16px', lineHeight: 1.45 },
        head: {
          fontSize: '0.8125rem', fontWeight: 600, color: t.muted, backgroundColor: t.surface,
          textTransform: 'none', letterSpacing: 0, whiteSpace: 'nowrap', padding: '11px 16px',
          borderBottom: `1px solid ${t.lineStrong}`,
        },
        sizeSmall: { padding: '10px 12px', fontSize: '0.875rem' },
        stickyHeader: { backgroundColor: t.surface },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': { backgroundColor: t.surface },
          '&:last-child > .MuiTableCell-body': { borderBottom: 0 },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: { root: { borderTop: `1px solid ${t.line}` }, toolbar: { fontSize: '0.875rem' }, selectLabel: { fontSize: '0.875rem' }, displayedRows: { fontSize: '0.875rem' } },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12, fontSize: '0.90625rem', lineHeight: 1.5, alignItems: 'flex-start', padding: '10px 14px',
          '& .MuiAlert-action .MuiIconButton-root': { color: 'inherit', opacity: 0.85, '&:hover': { opacity: 1, backgroundColor: 'rgba(28,23,38,0.06)' } },
        },
        icon: { paddingTop: 8, paddingBottom: 0, opacity: 1 },
        message: { paddingTop: 8, paddingBottom: 8 },
        action: { paddingTop: 2 },
        standardSuccess: { backgroundColor: t.successSoft, color: '#23604A', border: '1px solid #CFE6DA', '& .MuiAlert-icon': { color: t.success } },
        standardError: { backgroundColor: t.dangerSoft, color: '#8E302B', border: '1px solid #EFD3D0', '& .MuiAlert-icon': { color: t.danger } },
        standardWarning: { backgroundColor: t.warningSoft, color: '#6E470C', border: '1px solid #EBDDBE', '& .MuiAlert-icon': { color: t.warning } },
        standardInfo: { backgroundColor: t.plumSofter, color: t.inkSoft, border: `1px solid ${t.line}`, '& .MuiAlert-icon': { color: t.plum } },
        filled: { boxShadow: '0 12px 30px rgba(28,23,38,0.22)' },
        filledSuccess: { backgroundColor: t.ink, color: '#FFFFFF', '& .MuiAlert-icon': { color: '#7FD3A9' } },
        filledError: { backgroundColor: t.danger, color: '#FFFFFF' },
        filledWarning: { backgroundColor: '#8A5A10', color: '#FFFFFF' },
        filledInfo: { backgroundColor: t.ink, color: '#FFFFFF', '& .MuiAlert-icon': { color: t.champagne } },
        outlined: { backgroundColor: t.paper },
      },
    },
    MuiAlertTitle: {
      styleOverrides: { root: { fontWeight: 700, fontSize: '0.9375rem', marginBottom: 2 } },
    },
    MuiStepIcon: {
      styleOverrides: { root: { color: t.lineStrong, '&.Mui-active': { color: t.plum }, '&.Mui-completed': { color: t.plum } } },
    },
    MuiStepLabel: {
      styleOverrides: { label: { fontSize: '0.875rem', fontWeight: 600, '&.Mui-active': { fontWeight: 700 } } },
    },
    MuiDrawer: {
      styleOverrides: { paper: { backgroundImage: 'none' } },
    },
    MuiPaginationItem: {
      styleOverrides: { root: { borderRadius: 9, fontWeight: 600 } },
    },
  },
});
